// src/encounter/structured-note/system-lines-model.ts
//
// PROD-2947 · frame v2 · the 6 Oct contract's K5, amended 7 Oct 07:20 (the factory check measured 124 visible option
// controls in PE and 14 in ROS on preset-v4 content — per-finding toggles are an option wall, law L2):
//   · ONE line per SYSTEM. The system line is a K1 pick: ROS "Denies / Reports / Not asked", exam "Normal / Abnormal /
//     Not examined";
//   · "Reports" or "Abnormal" opens ONE K2 multi-pick of that system's findings (at most 8 rows, type to filter);
//   · the read view is text: "Reports nausea · Denies vomiting, fever";
//   · the complaint's systems come first, none hidden (the models' order, PROD-2789);
//   · "All negative" never touches a risk or suicide item;
//   · the answers save to the SAME questionnaire linkIds — only the controls change.
//
// THE WIRE IS THE SAME BOOLEAN PER FINDING (note-adapter.ts header): ROS true = reports, false = denies; exam +/- finding
// true = abnormal (present), false = not present; exam normal statement true = normal; unset = not asked / examined.
// The system's state is DERIVED from its findings — never stored anywhere else:
//   · positive (Reports / Abnormal) when any +/- finding is true;
//   · negative (Denies / Normal) when none is, and a finding is false or a normal statement true;
//   · none (Not asked / Not examined) when nothing is answered.
// Pure: no React.
import type { AnswerMap, AnswerUpdate } from './note-adapter';

export type SystemKind = 'ros' | 'exam';
export type SystemState = 'negative' | 'positive' | 'none';

/** One finding of a system: a +/- finding (`finding`) or, on the exam, a normal statement (`normal`). */
export interface SystemFinding {
  linkId: string;
  text: string;
  kind: 'finding' | 'normal';
  /** A risk or suicide item: never written by a system-wide choice ("All negative", "Denies", "Normal", "Not asked"). */
  risk: boolean;
}

export interface SystemLine {
  code: string;
  display: string;
  findings: readonly SystemFinding[];
}

/** The K1 states, in the contract's order, with the words each kind uses. */
export const STATE_OPTIONS: Readonly<Record<SystemKind, readonly { value: SystemState; label: string }[]>> = {
  ros: [
    { value: 'negative', label: 'Denies' },
    { value: 'positive', label: 'Reports' },
    { value: 'none', label: 'Not asked' },
  ],
  exam: [
    { value: 'negative', label: 'Normal' },
    { value: 'positive', label: 'Abnormal' },
    { value: 'none', label: 'Not examined' },
  ],
};

export const stateLabel = (kind: SystemKind, state: SystemState): string =>
  STATE_OPTIONS[kind].find((o) => o.value === state)?.label ?? '';

const answered = (v: AnswerMap[string]): boolean => v !== undefined && v !== null && v !== '';

/**
 * A risk or suicide finding is asked ONE AT A TIME (Codex P2 on K5): it is never part of its system's line, its pick or
 * its state, and it gets its own line with an explicit answer — present, absent or not asked — that no system-wide
 * choice ever writes.
 */
export const riskFindings = (line: SystemLine): SystemFinding[] => line.findings.filter((f) => f.risk);
const ownFindings = (line: SystemLine): SystemFinding[] => line.findings.filter((f) => !f.risk);

/** The findings the K2 multi-pick offers: every +/- finding that is not a risk item (an exam's normal statements are
 *  not abnormal findings). */
export const pickableFindings = (line: SystemLine): SystemFinding[] => ownFindings(line).filter((f) => f.kind === 'finding');

/**
 * PROD-3131 (design desk D-77): what "Normal" records on an EXAM system — its normal statements (risk items excluded), in
 * the content's order and words, joined — drawn under "Normal" inside the open list, so the clinician sees what it means
 * before picking it. Nothing is added to the unanswered line. ROS's "Denies" has no normal statements: undefined.
 */
export function normalDescription(kind: SystemKind, line: SystemLine): string | undefined {
  if (kind !== 'exam') return undefined;
  const statements = ownFindings(line).filter((f) => f.kind === 'normal').map((f) => f.text.trim()).filter((t) => t !== '');
  return statements.length > 0 ? statements.join(', ') : undefined;
}

/** The K1 states a system offers: "Reports" / "Abnormal" only when it has a finding to pick (Codex P1 on K5: a system of
 *  normal statements only cannot hold an abnormal answer on the boolean wire, so it is never offered). */
export function stateOptions(kind: SystemKind, line: SystemLine): readonly { value: SystemState; label: string }[] {
  const hasPick = pickableFindings(line).length > 0;
  // 20 Oct, Antonin: an exam system offers only Normal / Abnormal; "Not examined" stays the label of an untouched line (STATE_OPTIONS
  // keeps it for the read view) and a second click on the chosen chip clears it. ROS keeps Denies / Reports / Not asked.
  return STATE_OPTIONS[kind].filter((o) => (hasPick || o.value !== 'positive') && !(kind === 'exam' && o.value === 'none'));
}

/** The system's state, derived from its answers (its risk items have their own lines). */
export function systemState(line: SystemLine, values: AnswerMap): SystemState {
  const own = ownFindings(line);
  if (own.some((f) => f.kind === 'finding' && values[f.linkId] === true)) return 'positive';
  if (own.some((f) => (f.kind === 'finding' && values[f.linkId] === false) || (f.kind === 'normal' && values[f.linkId] === true))) {
    return 'negative';
  }
  return 'none';
}

/** The +/- findings answered "present" (Reports / Abnormal), in the system's order. */
export const positives = (line: SystemLine, values: AnswerMap): string[] =>
  pickableFindings(line).filter((f) => values[f.linkId] === true).map((f) => f.linkId);

/** A risk finding's own line: its explicit answer, in the system's words. */
export const RISK_OPTIONS: Readonly<Record<SystemKind, readonly { value: 'yes' | 'no' | 'unset'; label: string }[]>> = {
  ros: [
    { value: 'yes', label: 'Reports' },
    { value: 'no', label: 'Denies' },
    { value: 'unset', label: 'Not asked' },
  ],
  exam: [
    { value: 'yes', label: 'Present' },
    { value: 'no', label: 'Not present' },
    { value: 'unset', label: 'Not examined' },
  ],
};
/** The choices a risk line OFFERS: an exam risk line is Present / Not present only (20 Oct, Antonin); ROS keeps Not asked.
 *  RISK_OPTIONS stays whole for the read text. */
export const riskOptions = (kind: SystemKind): readonly { value: 'yes' | 'no' | 'unset'; label: string }[] =>
  RISK_OPTIONS[kind].filter((o) => !(kind === 'exam' && o.value === 'unset'));
export const riskValue = (v: AnswerMap[string]): 'yes' | 'no' | null => (v === true ? 'yes' : v === false ? 'no' : null);
export const riskWrite = (linkId: string, next: string): AnswerUpdate => ({ linkId, value: next === 'yes' ? true : next === 'no' ? false : undefined });
export const riskReadText = (kind: SystemKind, v: AnswerMap[string]): string =>
  RISK_OPTIONS[kind].find((o) => o.value === (riskValue(v) ?? 'unset'))?.label ?? '';

const changes = (values: AnswerMap, wanted: readonly AnswerUpdate[]): AnswerUpdate[] =>
  wanted.filter((u) => values[u.linkId] !== u.value && !(u.value === undefined && !answered(values[u.linkId])));

/**
 * What a system line's K1 choice writes. A risk item is never written by it (the "All negative" rule):
 *  · negative — ROS: every finding "denies" (false). Exam: every normal statement true; an abnormal finding recorded
 *    present goes to "not present"; when the system has no normal statement, every finding "not present" (so the
 *    system can read Normal at all);
 *  · positive — nothing by itself: it opens the findings pick, and a pick writes;
 *  · none — every answered finding cleared (not asked / not examined).
 */
export function stateChange(kind: SystemKind, line: SystemLine, values: AnswerMap, next: SystemState): AnswerUpdate[] {
  const own = line.findings.filter((f) => !f.risk);
  if (next === 'positive') return [];
  if (next === 'none') return changes(values, own.map((f) => ({ linkId: f.linkId, value: undefined })));
  if (kind === 'ros') return changes(values, own.map((f) => ({ linkId: f.linkId, value: false })));
  const hasNormal = own.some((f) => f.kind === 'normal');
  return changes(
    values,
    own.flatMap((f): AnswerUpdate[] => {
      if (f.kind === 'normal') return [{ linkId: f.linkId, value: true }];
      if (values[f.linkId] === true || !hasNormal) return [{ linkId: f.linkId, value: false }];
      return [];
    }),
  );
}

/**
 * What the K2 findings pick writes: each picked +/- finding "present" (true); one recorded present and no longer picked
 * goes back to unanswered. A finding picked by the clinician may be a risk item — that is her own, single answer.
 */
export function findingsChange(line: SystemLine, values: AnswerMap, picked: readonly string[]): AnswerUpdate[] {
  return changes(
    values,
    pickableFindings(line).flatMap((f): AnswerUpdate[] => {
      if (picked.includes(f.linkId)) return [{ linkId: f.linkId, value: true }];
      if (values[f.linkId] === true) return [{ linkId: f.linkId, value: undefined }];
      return [];
    }),
  );
}

/**
 * "Denies for the rest" (ROS) / "Normal on the rest" (exam) — the rule the blocks kept, on the systems the clinician has
 * ENGAGED (a system with an answer): never a system nothing was asked of, never a recorded answer, never a risk item.
 *  · ROS: each unanswered finding of an answered system "denies" (false);
 *  · exam: each unanswered normal statement of an answered system with no abnormal finding is set normal (true) — never
 *    a +/- finding (AC-08.1).
 */
export function restNegative(kind: SystemKind, lines: readonly SystemLine[], values: AnswerMap): AnswerUpdate[] {
  const out: AnswerUpdate[] = [];
  for (const line of lines) {
    const state = systemState(line, values);
    if (state === 'none' || (kind === 'exam' && state === 'positive')) continue;
    for (const f of line.findings) {
      if (f.risk || answered(values[f.linkId])) continue;
      if (kind === 'ros' && f.kind === 'finding') out.push({ linkId: f.linkId, value: false });
      if (kind === 'exam' && f.kind === 'normal') out.push({ linkId: f.linkId, value: true });
    }
  }
  return out;
}

/** The read view of a system: "Reports nausea · Denies vomiting, fever" (ROS); "Abnormal: … · Normal: … · Not present: …"
 *  (exam). Empty when nothing is answered. */
export function readText(kind: SystemKind, line: SystemLine, values: AnswerMap): string {
  const textOf = (pred: (f: SystemFinding) => boolean) => ownFindings(line).filter(pred).map((f) => f.text.trim());
  const parts: string[] = [];
  const present = textOf((f) => f.kind === 'finding' && values[f.linkId] === true);
  const absent = textOf((f) => f.kind === 'finding' && values[f.linkId] === false);
  const normal = textOf((f) => f.kind === 'normal' && values[f.linkId] === true);
  if (kind === 'ros') {
    if (present.length) parts.push(`Reports ${present.join(', ')}`);
    if (absent.length) parts.push(`Denies ${absent.join(', ')}`);
  } else {
    if (present.length) parts.push(`Abnormal: ${present.join(', ')}`);
    if (normal.length) parts.push(`Normal: ${normal.join(', ')}`);
    if (absent.length) parts.push(`Not present: ${absent.join(', ')}`);
  }
  return parts.join(' · ');
}
